import { Editor } from 'slate';
import { type as otSlateType, Operation } from '../ot-slate-v2';
import History from './History';

import { type Operation as SlateOperation } from 'slate';

export default class UndoEntry {
  operations: SlateOperation[] = [];
  pendingRemoteOperations: SlateOperation[] = [];
  startValue = null;

  constructor(operations: SlateOperation[] = [], options = { invert: false }) {
    if (options.invert) {
      operations = this._invertOperations(operations);

      /* TODO: Handle `isFocused`
      operations = operations.map((op) => {
        if (op.type !== 'set_selection') return op;
        return op.set('properties', omit(op.properties, 'isFocused'));
      });
      */
    }

    this.operations = operations;
  }

  // Apply the operations in this entry to the editor and return a new
  // UndoEntry that represents the inverse of this one.
  apply(editor: Editor): UndoEntry {
    History.withoutUndoTransforming(editor, (editor) =>
      History.withoutUndoSaving(editor, (editor) =>
        Editor.withoutNormalizing(editor, () =>
          this.operations.forEach((op) => editor.apply(op))
        )
      )
    );
    return new UndoEntry(this._invertOperations(this.operations));
  }

  tryCompose(op) {
    if (this.hasRemoteOperations) return false;

    const prevOperation = this.operations[0];
    if (!prevOperation) return false;

    const mergedOp = otSlateType.composeComponent(prevOperation, op);

    if (mergedOp === prevOperation) {
      return false;
    } else {
      this.operations[0] = mergedOp;
      return true;
    }
  }

  append(op: SlateOperation) {
    if (this.hasRemoteOperations) return false;
    this.operations.unshift(op);
    return true;
  }

  head(): SlateOperation {
    return this.operations[0];
  }

  get isEmpty() {
    return this.operations.length === 0;
  }

  get hasRemoteOperations() {
    return this.pendingRemoteOperations.length !== 0;
  }

  transform(): SlateOperation[] {
    if (this.pendingRemoteOperations.length === 0) {
      return [];
    }

    const [left, right] = otSlateType.transformX(
      this.operations,
      this.pendingRemoteOperations
    );

    this.clearRemoteOperations();
    this.operations = left;

    return right;
  }

  appendRemoteOperations(ops: SlateOperation[]) {
    if (ops.length === 0) return;

    this.pendingRemoteOperations = this.pendingRemoteOperations.concat(ops);
  }

  clearRemoteOperations() {
    this.pendingRemoteOperations = [];
    this.startValue = null;
  }

  // Invert an item in an undo or redo stack, to prepare it for
  // pushing onto the other stack.
  _invertOperations(operations) {
    return operations
      .reverse()
      .map((operation) => Operation.inverse(operation));
  }
}
